import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { api, filePreviewRelativeUrl } from '../lib/api'
import {
  CLAIM_FLOW_COLLECTED_BIND_KEYS,
  isClaimFlowCollectedBindKey,
} from '../lib/claimFlowTemplateBindKeys'
import { useToast } from '../ui/ToastProvider'
import {
  CLAIM_INSURER_CATEGORY_FILTERS,
  claimInsurerCategoryLabel,
  matchesClaimInsurerCategory,
  useClaimAdminInsurers,
} from '../hooks/useClaimAdminInsurers'
import { formatApiDateTimeDisplay } from '../lib/date'
import MenuGuideButton from '../components/MenuGuideButton'
import { PdfTemplateMapper } from './Customers'
import { useAuth } from '../auth/AuthContext'
import { buildClaimTemplatePreviewValues } from '../lib/claimTemplatePreviewSamples'
import { PDF_MAPPING_FONT_OPTIONS } from '../lib/pdfMappingFontOptions'
import {
  CLAIM_ADDITIONAL_FIELD_TYPES,
  blankClaimAdditionalField,
  isChoiceAdditionalFieldType,
  prepareAdditionalFieldForEdit,
  prepareAdditionalFieldForSave,
} from '../lib/claimAdditionalFields'
// 일단 기본 구조만 만들고, PdfTemplateMapper는 나중에 공통화하겠습니다.

function formatTemplateUpdatedAt(iso) {
  return formatApiDateTimeDisplay(undefined, iso)
}

/** 다운로드 파일명용: Windows 금지 문자 제거 */
function safeFilenamePart(s, maxLen = 48) {
  const x = String(s ?? '')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .trim()
  if (!x) return 'insurer'
  return x.length > maxLen ? x.slice(0, maxLen) : x
}

function getInsurerLogoSrc(org) {
  if (!org) return null
  if (org.claimBrandLogoFileId) return filePreviewRelativeUrl(org.claimBrandLogoFileId)
  if (org.logoUrl) return org.logoUrl
  if (org.logoImageUrl) return org.logoImageUrl
  if (org.logoFileId) return filePreviewRelativeUrl(org.logoFileId)
  if (org.fileId) return filePreviewRelativeUrl(org.fileId)
  return null
}

function isTemplateVCheckField(field) {
  const vt = String(field?.valueType || '').trim().toUpperCase()
  if (vt === 'CHECK_V') return true
  return String(field?.bindKey || '').trim() === 'defaultVCheck'
}

export default function ClaimFormTemplatesPage({ embedded = false }) {
  const { show } = useToast()
  const { user } = useAuth()
  const {
    allInsurers,
    lifeRootId,
    nonlifeRootId,
    petRootId,
    liabilityRootId,
    loading: insurersLoading,
  } = useClaimAdminInsurers()

  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [selectedInsurerOrgId, setSelectedInsurerOrgId] = useState(null)
  const [templateStatusById, setTemplateStatusById] = useState({})
  const [templateFileId, setTemplateFileId] = useState('')
  const [uploadingTemplate, setUploadingTemplate] = useState(false)
  const [fields, setFields] = useState([])
  const [selectedFieldKey, setSelectedFieldKey] = useState(null)
  const [saving, setSaving] = useState(false)
  const [mappingBusy, setMappingBusy] = useState(false)
  /** false: 다른 환경 이전용 — JSON에 templateFileId 제외(운영에서 활성 PDF에 좌표만 반영) */
  const [mappingExportIncludeFileId, setMappingExportIncludeFileId] = useState(false)
  /** true: 선택 보험사 + fileId 무시(활성 템플릿에 좌표만). false: JSON의 insurerOrgId·fileId 그대로(동일 환경·일괄) */
  const [mappingImportCrossEnvMode, setMappingImportCrossEnvMode] = useState(true)
  /** 보험사별 좌표 초기화 요청 중인 org id */
  const [resettingCoordinatesOrgId, setResettingCoordinatesOrgId] = useState(null)
  /** 보험사별 청구 활성 토글 요청 중인 org id */
  const [claimToggleBusyOrgId, setClaimToggleBusyOrgId] = useState(null)
  const [formVersion, setFormVersion] = useState('')
  const [faxPageRangesText, setFaxPageRangesText] = useState('')
  /** 템플릿 전역 기본 PDF 폰트 — 필드 fontKey가 비었을 때 출력·미리보기에 사용 */
  const [defaultPdfFontKey, setDefaultPdfFontKey] = useState('')
  /** 필드 그룹 중 현재 펼친 섹션 1개만 표시 */
  const [activeFieldSectionKey, setActiveFieldSectionKey] = useState(null)
  /** 필드 편집(테이블) 영역 접기 — 기본 접힘 */
  const [fieldEditorOpen, setFieldEditorOpen] = useState(false)
  /** 필드 편집 표에서 V 체크(CHECK_V) 행 숨김 — 기본 켜짐 */
  const [hideVCheckInFieldEditor, setHideVCheckInFieldEditor] = useState(true)
  /** PdfTemplateMapper 현재 페이지(0-based) — 새 필드·기본 V 추가 시 반영 */
  const [pdfPageIndex, setPdfPageIndex] = useState(0)
  /** 상단 보험사 목록 패널 접기(좌표 편집 영역 확보) */
  const [insurerPickerOpen, setInsurerPickerOpen] = useState(true)
  /** PDF 오버레이에서 매핑 필드 라벨(표시명) 배지 표시 */
  const [showPdfMappingFieldLabels, setShowPdfMappingFieldLabels] = useState(true)
  /** PDF 위 샘플 입력값 오버레이 */
  const [pdfCoordinatePreviewEnabled, setPdfCoordinatePreviewEnabled] = useState(true)
  const [additionalFields, setAdditionalFields] = useState([])
  const [additionalFieldsLoading, setAdditionalFieldsLoading] = useState(false)
  const [additionalFieldsSaving, setAdditionalFieldsSaving] = useState(false)
  const [additionalFieldsOpen, setAdditionalFieldsOpen] = useState(false)

  const claimPreviewValues = useMemo(() => buildClaimTemplatePreviewValues(user), [user])

  const claimFlowBindKeySet = useMemo(() => new Set(CLAIM_FLOW_COLLECTED_BIND_KEYS), [])

  const vCheckFieldCount = useMemo(
    () => fields.filter(isTemplateVCheckField).length,
    [fields],
  )

  const fieldEditorRows = useMemo(() => {
    const rows = fields.map((f, idx) => ({ f, idx }))
    if (!hideVCheckInFieldEditor) return rows
    return rows.filter(({ f }) => !isTemplateVCheckField(f))
  }, [fields, hideVCheckInFieldEditor])
  /** 필드 선택 UI 하단 — 보험금 청구 메뉴 입력 항목 모음(접기 키) */
  const claimFlowDirectInputSectionTitle = '직접입력필드'

  const filteredInsurers = useMemo(() => {
    if (categoryFilter === 'ALL') return allInsurers
    return allInsurers.filter((o) =>
      matchesClaimInsurerCategory(o, categoryFilter, lifeRootId, nonlifeRootId, petRootId, liabilityRootId))
  }, [allInsurers, categoryFilter, lifeRootId, nonlifeRootId, petRootId, liabilityRootId])

  const selectedInsurer = useMemo(() => {
    if (!selectedInsurerOrgId) return null
    return allInsurers.find((o) => String(o.id) === String(selectedInsurerOrgId)) || null
  }, [allInsurers, selectedInsurerOrgId])

  const mergeTemplateStatuses = useCallback(async (idList) => {
    const ids = [...new Set((idList || []).filter((x) => x != null))]
    if (!ids.length) return
    try {
      const q = new URLSearchParams()
      ids.forEach((id) => q.append('insurerOrgIds', id))
      const res = await api.get(`/claims/form-templates/statuses?${q.toString()}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const rows = Array.isArray(payload) ? payload : []
      const patch = {}
      for (const row of rows) {
        const id = row?.insurerOrgId
        if (id == null) continue
        patch[String(id)] = {
          hasTemplate: !!row.hasTemplate,
          claimEnabled: !!row.claimEnabled,
          updatedAt: row.updatedAt || null,
          formVersion: row.formVersion || '',
          expectedPageCount: row.expectedPageCount ?? null,
          reason: row.reason || null,
        }
      }
      setTemplateStatusById((prev) => ({ ...prev, ...patch }))
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!filteredInsurers.length) {
        if (!cancelled) setTemplateStatusById({})
        return
      }
      const q = new URLSearchParams()
      filteredInsurers.forEach((o) => q.append('insurerOrgIds', o.id))
      try {
        const res = await api.get(`/claims/form-templates/statuses?${q.toString()}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const rows = Array.isArray(payload) ? payload : []
        const next = {}
        for (const row of rows) {
          const id = row?.insurerOrgId
          if (id == null) continue
          next[String(id)] = {
            hasTemplate: !!row.hasTemplate,
            claimEnabled: !!row.claimEnabled,
            updatedAt: row.updatedAt || null,
            formVersion: row.formVersion || '',
            expectedPageCount: row.expectedPageCount ?? null,
            reason: row.reason || null,
          }
        }
        if (!cancelled) setTemplateStatusById(next)
      } catch {
        if (!cancelled) setTemplateStatusById({})
      }
    })()
    return () => { cancelled = true }
  }, [filteredInsurers])

  useEffect(() => {
    if (!selectedInsurerOrgId) return
    const ok = filteredInsurers.some((o) => String(o.id) === String(selectedInsurerOrgId))
    if (!ok) setSelectedInsurerOrgId(null)
  }, [filteredInsurers, selectedInsurerOrgId])

  useEffect(() => {
    setActiveFieldSectionKey(null)
    if (selectedInsurerOrgId) {
      loadActiveTemplate()
      loadAdditionalFields()
    } else {
      setTemplateFileId('')
      setFields([])
      setAdditionalFields([])
      setPdfPageIndex(0)
    }
  }, [selectedInsurerOrgId])

  useEffect(() => {
    if (templateFileId) {
      setActiveFieldSectionKey((prev) => prev ?? '피보험자')
    }
  }, [templateFileId])

  const resetCoordinatesForInsurer = async (org, e) => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    const st = templateStatusById[String(org.id)]
    if (!st?.hasTemplate) {
      show('활성 템플릿이 없어 초기화할 좌표가 없습니다.', 'error')
      return
    }
    const ok = window.confirm(
      `「${org.name}」의 청구서 좌표(필드 매핑)를 모두 비웁니다.\n템플릿 PDF·팩스 페이지 설정은 유지됩니다. 계속할까요?`,
    )
    if (!ok) return
    setResettingCoordinatesOrgId(org.id)
    try {
      await api.post('/claims/form-templates/mappings/reset-fields', null, {
        params: { insurerOrgId: org.id },
      })
      show(`「${org.name}」 좌표를 초기화했습니다.`, 'success')
      await mergeTemplateStatuses([org.id])
      if (String(selectedInsurerOrgId) === String(org.id)) {
        setFields([])
        await loadActiveTemplate()
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || ''
      if (String(msg).includes('ACTIVE_TEMPLATE_NOT_FOUND') || err?.response?.status === 404) {
        show('활성 템플릿을 찾을 수 없습니다.', 'error')
      } else {
        show('좌표 초기화에 실패했습니다.', 'error')
      }
    } finally {
      setResettingCoordinatesOrgId(null)
    }
  }

  const toggleClaimEnabledForInsurer = async (org, enabled, e) => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    const st = templateStatusById[String(org.id)]
    if (!st?.hasTemplate) {
      show('청구 서식이 없어 활성화할 수 없습니다. 먼저 템플릿 PDF와 좌표를 저장해 주세요.', 'error')
      return
    }
    setClaimToggleBusyOrgId(org.id)
    try {
      await api.post('/claims/form-templates/claim-enabled', null, {
        params: { insurerOrgId: org.id, enabled },
      })
      show(`「${org.name}」 보험금청구를 ${enabled ? '활성화' : '비활성화'}했습니다.`, 'success')
      await mergeTemplateStatuses([org.id])
    } catch {
      show('청구 활성 상태 변경에 실패했습니다.', 'error')
    } finally {
      setClaimToggleBusyOrgId(null)
    }
  }

  const loadActiveTemplate = async () => {
    if (!selectedInsurerOrgId) return
    try {
      const res = await api.get('/claims/form-templates/active', { params: { insurerOrgId: selectedInsurerOrgId } })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      if (payload) {
        setTemplateFileId(payload.templateFileId || '')
        setFormVersion(payload.formVersion || '')
        setDefaultPdfFontKey(payload.defaultPdfFontKey || '')
        const faxPages = (() => {
          try {
            const raw = payload.faxPageRangesJson
            const arr = typeof raw === 'string' ? JSON.parse(raw) : raw
            return Array.isArray(arr) ? arr.join(',') : ''
          } catch {
            return ''
          }
        })()
        setFaxPageRangesText(faxPages)
        try {
          const fieldsJson = payload.fieldsJson
          if (fieldsJson) {
            const parsed = typeof fieldsJson === 'string' ? JSON.parse(fieldsJson) : fieldsJson
            setFields(Array.isArray(parsed) ? parsed : [])
          } else {
            setFields([])
          }
        } catch {
          setFields([])
        }
      } else {
        setTemplateFileId('')
        setFields([])
        setDefaultPdfFontKey('')
      }
    } catch (err) {
      // 404는 템플릿이 없을 때 정상적인 응답이므로 에러로 처리하지 않음
      if (err?.response?.status === 404) {
        setTemplateFileId('')
        setFields([])
        setFaxPageRangesText('')
        setDefaultPdfFontKey('')
      } else {
        // 다른 에러는 로그만 남기고 상태 초기화
        console.warn('Failed to load active template:', err)
        setTemplateFileId('')
        setFields([])
        setFaxPageRangesText('')
        setDefaultPdfFontKey('')
      }
    }
  }

  const loadAdditionalFields = async () => {
    if (!selectedInsurerOrgId) return
    setAdditionalFieldsLoading(true)
    try {
      const res = await api.get('/claims/additional-fields/admin', {
        params: { insurerOrgId: selectedInsurerOrgId },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const rows = Array.isArray(payload) ? payload : []
      setAdditionalFields(rows.map((row, idx) => prepareAdditionalFieldForEdit(row, idx)))
    } catch {
      setAdditionalFields([])
      show('추가 입력 항목을 불러오지 못했습니다.', 'error')
    } finally {
      setAdditionalFieldsLoading(false)
    }
  }

  const addAdditionalField = () => {
    setAdditionalFields((prev) => [...prev, blankClaimAdditionalField(prev.length)])
  }

  const updateAdditionalField = (idx, patch) => {
    setAdditionalFields((prev) => prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)))
  }

  const removeAdditionalField = (idx) => {
    setAdditionalFields((prev) => prev.filter((_, i) => i !== idx).map((row, i) => ({ ...row, sortOrder: i })))
  }

  const saveAdditionalFields = async () => {
    if (!selectedInsurerOrgId) {
      show('보험사를 선택해주세요.', 'error')
      return
    }
    const rows = additionalFields.map((row, idx) => prepareAdditionalFieldForSave(row, idx))
    for (const row of rows) {
      if (!row.fieldKey || !row.label) {
        show('추가 입력 항목의 필드 키와 항목명을 입력해 주세요.', 'error')
        return
      }
      if (isChoiceAdditionalFieldType(row.inputType) && (!row.options || row.options.length === 0)) {
        show('선택형 추가 입력 항목에는 선택지를 입력해 주세요.', 'error')
        return
      }
    }
    setAdditionalFieldsSaving(true)
    try {
      const res = await api.put(`/claims/additional-fields/admin/${selectedInsurerOrgId}`, { fields: rows })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const saved = Array.isArray(payload) ? payload : []
      setAdditionalFields(saved.map((row, idx) => prepareAdditionalFieldForEdit(row, idx)))
      show('추가 입력 항목을 저장했습니다.', 'success')
    } catch (e) {
      const msg = e?.response?.data?.message || ''
      show(msg || '추가 입력 항목 저장에 실패했습니다.', 'error')
    } finally {
      setAdditionalFieldsSaving(false)
    }
  }

  const handleTemplateUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.type !== 'application/pdf') {
      show('PDF 파일만 업로드 가능합니다.', 'error')
      return
    }
    setUploadingTemplate(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post('/files', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const fileId = payload?.fileId || payload?.id
      if (fileId) {
        setTemplateFileId(fileId)
        show('템플릿 PDF가 업로드되었습니다.', 'success')
      }
    } catch (e) {
      show('템플릿 PDF 업로드에 실패했습니다.', 'error')
    } finally {
      setUploadingTemplate(false)
      e.target.value = ''
    }
  }

  const downloadTemplateFile = async () => {
    if (!templateFileId) return
    try {
      const res = await api.get(`/files/${templateFileId}/download`, { responseType: 'blob' })
      const blob = new Blob([res.data], { type: res?.headers?.['content-type'] || 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url

      const cd = res?.headers?.['content-disposition'] || res?.headers?.['Content-Disposition'] || ''
      const filenameMatch = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="([^"]+)"/i)
      const filename = filenameMatch ? decodeURIComponent(filenameMatch[1]) : `claim_template_${templateFileId}.pdf`

      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      show('템플릿 다운로드에 실패했습니다.', 'error')
    }
  }

  const ensureField = (key) => {
    if (!key) return
    // 동일한 필드를 여러번 추가할 수 있도록 중복 체크 제거
    // 고유한 key를 순번으로 생성 (key_1, key_2, ...)
    const existingKeys = fields.map(f => f.key).filter(k => k && k.startsWith('key_'))
    const maxNum = existingKeys.reduce((max, k) => {
      const match = k.match(/^key_(\d+)$/)
      if (match) {
        const num = parseInt(match[1], 10)
        return Math.max(max, num)
      }
      return max
    }, 0)
    const uniqueKey = `key_${maxNum + 1}`
    setFields([...fields, {
      key: uniqueKey,
      bindKey: key, // 원본 필드명은 bindKey에 저장
      pageIndex: pdfPageIndex,
      x: 0.1,
      y: 0.1,
      w: 0.2,
      h: 0.03,
      fontSize: 12,
      align: 'left',
      type: 'text',
      valueType: '',
      value: '',
      format: '', // phone, residentNumber
      fontKey: '',
    }])
    setSelectedFieldKey(uniqueKey)
  }

  /** 고객/설계사 입력 없이 청구 PDF에 항상 V가 찍히는 좌표용 필드 */
  const addDefaultCheckVField = () => {
    const existingKeys = fields.map(f => f.key).filter(k => k && k.startsWith('key_'))
    const maxNum = existingKeys.reduce((max, k) => {
      const match = k.match(/^key_(\d+)$/)
      if (match) {
        const num = parseInt(match[1], 10)
        return Math.max(max, num)
      }
      return max
    }, 0)
    const uniqueKey = `key_${maxNum + 1}`
    setFields([...fields, {
      key: uniqueKey,
      bindKey: 'defaultVCheck',
      pageIndex: pdfPageIndex,
      x: 0.1,
      y: 0.1,
      w: 0.035,
      h: 0.028,
      fontSize: 11,
      align: 'center',
      type: 'text',
      valueType: 'CHECK_V',
      value: '',
      format: '',
      fontKey: '',
    }])
    setSelectedFieldKey(uniqueKey)
  }

  const addQuickVCheckField = ({ pageIndex, x, y }) => {
    const existingKeys = fields.map(f => f.key).filter(k => k && k.startsWith('key_'))
    const maxNum = existingKeys.reduce((max, k) => {
      const match = k.match(/^key_(\d+)$/)
      if (match) {
        const num = parseInt(match[1], 10)
        return Math.max(max, num)
      }
      return max
    }, 0)
    const uniqueKey = `key_${maxNum + 1}`
    setFields([...fields, {
      key: uniqueKey,
      bindKey: 'defaultVCheck',
      pageIndex: Math.max(0, Number(pageIndex || 0)),
      x: Math.max(0, Math.min(1, Number(x || 0))),
      y: Math.max(0, Math.min(1, Number(y || 0))),
      w: 0.035,
      h: 0.028,
      fontSize: 11,
      align: 'center',
      type: 'text',
      valueType: 'CHECK_V',
      value: '',
      format: '',
      fontKey: '',
    }])
    setSelectedFieldKey(uniqueKey)
  }

  /** 고객/설계사 입력 없이 청구 PDF에 항상 '상동'이 찍히는 좌표용 필드 */
  const addDefaultSameAsAboveField = () => {
    const existingKeys = fields.map(f => f.key).filter(k => k && k.startsWith('key_'))
    const maxNum = existingKeys.reduce((max, k) => {
      const match = k.match(/^key_(\d+)$/)
      if (match) {
        const num = parseInt(match[1], 10)
        return Math.max(max, num)
      }
      return max
    }, 0)
    const uniqueKey = `key_${maxNum + 1}`
    setFields([...fields, {
      key: uniqueKey,
      bindKey: 'defaultSameAsAbove',
      pageIndex: pdfPageIndex,
      x: 0.1,
      y: 0.1,
      w: 0.07,
      h: 0.03,
      fontSize: 10,
      align: 'center',
      type: 'text',
      valueType: 'SAME_AS_ABOVE',
      value: '',
      format: '',
      fontKey: '',
    }])
    setSelectedFieldKey(uniqueKey)
  }

  const updateField = (key, updates) => {
    setFields(fields.map(f => f.key === key ? { ...f, ...updates } : f))
  }

  const updateFields = (updates) => {
    const patchByKey = new Map((updates || []).map((item) => [item.key, item.box || {}]))
    setFields((prev) => prev.map((f) => (patchByKey.has(f.key) ? { ...f, ...patchByKey.get(f.key) } : f)))
  }

  const handleSave = async () => {
    if (!selectedInsurerOrgId) {
      show('보험사를 선택해주세요.', 'error')
      return
    }
    if (!templateFileId) {
      show('템플릿 PDF를 업로드해주세요.', 'error')
      return
    }
    if (fields.length === 0) {
      show('최소 1개 이상의 필드를 추가해주세요.', 'error')
      return
    }

    const parseFaxPageRanges = (text) => {
      const s = (text || '').trim()
      if (!s) return null // 기본: 전체 페이지
      const out = []
      for (const part of s.split(',')) {
        const t = part.trim()
        if (!t) continue
        if (t.includes('-')) {
          const [a, b] = t.split('-').map((x) => Number(x.trim()))
          if (Number.isFinite(a) && Number.isFinite(b) && a > 0 && b > 0) {
            out.push({ from: a, to: b })
          }
        } else {
          const n = Number(t)
          if (Number.isFinite(n) && n > 0) out.push(n)
        }
      }
      return out.length > 0 ? out : null
    }

    setSaving(true)
    try {
      await api.post('/claims/form-templates', {
        insurerOrgId: Number(selectedInsurerOrgId),
        formVersion: formVersion || null,
        templateFileId,
        fields,
        faxPageRanges: parseFaxPageRanges(faxPageRangesText),
        defaultPdfFontKey: defaultPdfFontKey || null,
      })
      show('템플릿이 저장되었습니다. 사용자 청구에 노출하려면 청구활성을 켜 주세요.', 'success')
      await mergeTemplateStatuses([selectedInsurerOrgId])
      await loadActiveTemplate()
    } catch (e) {
      show('템플릿 저장에 실패했습니다.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const downloadMappingsJsonForSelectedInsurer = async () => {
    if (!selectedInsurerOrgId) {
      show('보험사를 먼저 선택한 뒤, 해당 보험사 좌표만 내려받을 수 있습니다.', 'error')
      return
    }
    setMappingBusy(true)
    try {
      const res = await api.get('/claims/form-templates/mappings/export', {
        params: { insurerOrgIds: [selectedInsurerOrgId] },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const cloned = JSON.parse(JSON.stringify(payload || {}))
      const items = Array.isArray(cloned.items) ? cloned.items : []
      if (items.length === 0) {
        show('선택한 보험사에 활성 템플릿이 없습니다. 먼저 PDF와 좌표를 저장하거나 다른 보험사를 선택하세요.', 'error')
        return
      }
      if (!mappingExportIncludeFileId) {
        for (const row of items) {
          if (row && typeof row === 'object') delete row.templateFileId
        }
      }
      cloned.format = 'claim_form_mappings_per_insurer'
      cloned.note = mappingExportIncludeFileId
        ? '동일 환경 복제용: templateFileId 포함. 다른 서버에서는 보험사 조직 ID·파일 ID가 달라 그대로 쓰면 안 됩니다.'
        : '환경 간 이전용: templateFileId 제외. 가져올 때 해당 보험사를 선택하고 활성 템플릿 PDF가 있어야 좌표만 반영됩니다.'
      const json = JSON.stringify(cloned, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const namePart = safeFilenamePart(selectedInsurer?.name || String(selectedInsurerOrgId))
      const day = new Date().toISOString().slice(0, 10)
      a.download = `claim_form_mapping_${namePart}_${day}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show(`선택 보험사 좌표 JSON을 내려받았습니다. (${items.length}건)`, 'success')
    } catch {
      show('좌표 JSON 다운로드에 실패했습니다.', 'error')
    } finally {
      setMappingBusy(false)
    }
  }

  const downloadAllMappingsBackup = async () => {
    setMappingBusy(true)
    try {
      const ids = allInsurers.map((o) => o.id).filter(Boolean)
      const res = await api.get('/claims/form-templates/mappings/export', {
        params: { insurerOrgIds: ids },
      })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const cloned = JSON.parse(JSON.stringify(payload || {}))
      cloned.format = 'claim_form_mappings_bulk_backup'
      cloned.note = '동일 환경 전체 백업용: insurerOrgId·templateFileId가 모두 포함됩니다.'
      const json = JSON.stringify(cloned, null, 2)
      const blob = new Blob([json], { type: 'application/json' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `claim_form_mappings_all_${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      show('전체 보험사 좌표 백업 JSON을 내려받았습니다.', 'success')
    } catch {
      show('전체 백업 다운로드에 실패했습니다.', 'error')
    } finally {
      setMappingBusy(false)
    }
  }

  const importMappingsJson = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setMappingBusy(true)
    try {
      const text = await file.text()
      const parsed = JSON.parse(text)
      
      // 여러 형식 지원:
      // 1. { items: [...] } - 백엔드 export 형식
      // 2. { fields: [...] } - 단일 보험사 fields 배열
      // 3. [...] - 배열 직접
      let items = []
      
      if (Array.isArray(parsed?.items)) {
        items = parsed.items.map((row) => ({ ...row }))
      } else if (Array.isArray(parsed?.fields)) {
        if (!selectedInsurerOrgId) {
          show('보험사를 먼저 선택해주세요.', 'error')
          return
        }
        const templateFileIdToUse = parsed.templateFileId || parsed.templatePdfFileId || templateFileId || null
        items = [{
          insurerOrgId: selectedInsurerOrgId,
          formVersion: parsed.formVersion || null,
          templateFileId: templateFileIdToUse,
          fields: parsed.fields,
          faxPageRanges: parsed.faxPageRanges || null,
          defaultPdfFontKey: parsed.defaultPdfFontKey ?? null,
        }]
      } else if (Array.isArray(parsed)) {
        items = parsed.map((row) => ({ ...row }))
      }
      
      if (items.length === 0) {
        show('가져올 items가 없습니다. JSON 형식을 확인해주세요.', 'error')
        return
      }

      if (mappingImportCrossEnvMode) {
        if (!selectedInsurerOrgId) {
          show('다른 환경 반영 모드에서는 가져올 대상 보험사를 먼저 선택해 주세요.', 'error')
          return
        }
        if (items.length !== 1) {
          show(
            '선택 보험사에만 반영하려면 JSON에 보험사 1건(항목 1개)만 있어야 합니다. 일괄 파일이면 아래 체크를 해제하세요.',
            'error',
          )
          return
        }
        const row = items[0]
        items = [{
          insurerOrgId: Number(selectedInsurerOrgId),
          formVersion: row.formVersion ?? null,
          templateFileId: null,
          fields: row.fields,
          faxPageRanges: row.faxPageRanges ?? null,
          defaultPdfFontKey: row.defaultPdfFontKey ?? null,
        }]
      }
      
      const payloadItems = items.map((row) => ({
        insurerOrgId: row.insurerOrgId != null ? Number(row.insurerOrgId) : null,
        formVersion: row.formVersion ?? null,
        templateFileId: row.templateFileId ?? null,
        fields: row.fields,
        faxPageRanges: row.faxPageRanges ?? null,
        defaultPdfFontKey: row.defaultPdfFontKey ?? null,
      }))

      const res = await api.post('/claims/form-templates/mappings/import', { items: payloadItems })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      
      // 결과 확인
      const successCount = payload?.success ?? 0
      const failedCount = payload?.failed ?? 0
      const results = payload?.results || []
      
      // 실패한 항목이 있으면 에러 메시지 표시
      if (failedCount > 0) {
        const errors = results
          .filter(r => !r.ok)
          .map(r => {
            const errorMsg = r.error || '알 수 없는 오류'
            // 특정 에러 메시지를 더 친화적으로 변환
            if (errorMsg.includes('ACTIVE_TEMPLATE_NOT_FOUND') || errorMsg.includes('templateFileId is required')) {
              return '템플릿이 없습니다. 먼저 PDF 파일을 업로드해주세요.'
            }
            return errorMsg
          })
          .join(', ')
        show(`좌표 가져오기 실패: ${errors}`, 'error')
      } else if (successCount > 0) {
        show(`좌표 가져오기 완료: ${successCount}개 성공`, 'success')
      }
      
      // 템플릿이 성공적으로 생성/업데이트되었으면 다시 로드
      if (selectedInsurerOrgId && successCount > 0) {
        await loadActiveTemplate()
        const touchedIds = [...new Set(results.filter((r) => r?.ok && r?.insurerOrgId != null).map((r) => r.insurerOrgId))]
        if (touchedIds.length) await mergeTemplateStatuses(touchedIds)
      }
    } catch (err) {
      console.error('JSON import error:', err)
      show('좌표 매핑 JSON 가져오기에 실패했습니다: ' + (err.message || '알 수 없는 오류'), 'error')
    } finally {
      setMappingBusy(false)
      e.target.value = ''
    }
  }

  // 필드 섹션별 그룹화
  const fieldSections = [
    {
      title: '피보험자',
      fields: [
        'customerName',
        'customerResidentNumber',
        'customerResidentNumber_1',
        'customerResidentNumber_2',
        'customerResidentNumber_rearDigit1',
        'customerResidentNumber_rearDigits2',
        'customerBirthDate',
        'customerPhone',
        'customerPhone_1',
        'customerPhone_2',
        'customerPhone_3',
        'customerEmail',
        'customerAddress',
        'customerWorkplace',
        'customerOccupation',
        'customerSignature',
      ]
    },
    {
      title: '계약자',
      fields: [
        'contractHolderSameAsInsured',
        'contractHolderName',
        'contractHolderResidentNumber',
        'contractHolderResidentNumber_1',
        'contractHolderResidentNumber_2',
        'contractHolderResidentNumber_rearDigit1',
        'contractHolderResidentNumber_rearDigits2',
        'contractHolderPhone',
        'contractHolderPhone_1',
        'contractHolderPhone_2',
        'contractHolderPhone_3',
        'contractHolderSignature',
        'contractHolderSignatureImageBase64',
      ]
    },
    {
      title: '의료수급권자',
      fields: [
        'medicalBenefitRecipient',
        'medicalBenefitRecipient_Y',
        'medicalBenefitRecipient_N',
      ]
    },
    {
      title: '운전여부',
      fields: [
        'drivingLicense',
        'drivingLicense_Y',
        'drivingLicense_N',
      ]
    },
    {
      title: '수익자',
      fields: [
        'beneficiaryName',
        'beneficiaryRelationToInsured',
        'beneficiaryResidentNo',
        'beneficiaryResidentNumber',
        'beneficiaryResidentNumber_1',
        'beneficiaryResidentNumber_2',
        'beneficiaryResidentNumber_rearDigit1',
        'beneficiaryResidentNumber_rearDigits2',
        'beneficiaryPhone',
        'beneficiaryPhone_1',
        'beneficiaryPhone_2',
        'beneficiaryPhone_3',
        'beneficiaryEmail',
        'beneficiaryAddress',
        'beneficiaryWorkplace',
        'beneficiaryOccupation',
      ]
    },
    {
      title: '수익자',
      fields: [
        'beneficiarySameAsCustomer',
        'beneficiarySameAsContractHolder',
        'beneficiaryIdentityType',
        'beneficiaryResidentIdIssueDate',
        'beneficiaryResidentIdIssueDateYear',
        'beneficiaryResidentIdIssueDateYear2',
        'beneficiaryResidentIdIssueDateMonth',
        'beneficiaryResidentIdIssueDateDay',
        'beneficiaryResidentIdIssuer',
        'beneficiaryDriverLicenseNumber',
        'beneficiaryDriverLicenseNumberRegion',
        'beneficiaryDriverLicenseNumberSecond',
        'beneficiaryDriverLicenseNumberThird',
        'beneficiaryDriverLicenseNumberFourth',
        'beneficiaryDriverLicenseSerialNumber',
        'beneficiaryDriverLicenseIssueDate',
        'beneficiaryDriverLicenseIssuer',
        'beneficiarySignature',
      ]
    },
    {
      title: '보상안내',
      fields: [
        'benefitNotificationMethod',
        'benefitNotificationMethodOption',
        'benefitNotificationRecipient',
        'benefitGuidePlannerName',
        'benefitGuidePlannerEmail',
        'benefitGuidePlannerResidentNumber',
        'benefitGuidePlannerResidentNumber_1',
        'benefitGuidePlannerResidentNumber_2',
      ]
    },
    {
      title: '보험금수령계좌',
      fields: [
        'accountPayoutType',
        'bankAutoTransfer',
        'bankPrepaidAccount',
        'bankName',
        'accountNumber',
        'accountHolder',
      ]
    },
    {
      title: '보험사별 추가',
      fields: [
        'virtualFaxNumber',
        'postalFaxNumber',
        'faxNumberSelection',
        'injuryDiseaseTreatmentSupport',
        'groupActualMedicalInsurance',
        'insuredJobDetail',
        'identityType',
        'residentIdIssueDate',
        'residentIdIssueDateYear',
        'residentIdIssueDateYear2',
        'residentIdIssueDateMonth',
        'residentIdIssueDateDay',
        'residentIdIssuer',
        'driverLicenseNumber',
        'driverLicenseNumberRegion',
        'driverLicenseNumberSecond',
        'driverLicenseNumberThird',
        'driverLicenseNumberFourth',
        'driverLicenseSerialNumber',
        'driverLicenseIssueDate',
        'driverLicenseIssuer',
        'minorBeneficiary',
        'guardianSelection',
        'guardianFatherName',
        'guardianFatherResidentNumber',
        'guardianFatherResidentNumber_1',
        'guardianFatherResidentNumber_2',
        'guardianFatherPhone',
        'guardianFatherPhone_1',
        'guardianFatherPhone_2',
        'guardianFatherPhone_3',
        'guardianMotherName',
        'guardianMotherResidentNumber',
        'guardianMotherResidentNumber_1',
        'guardianMotherResidentNumber_2',
        'guardianMotherPhone',
        'guardianMotherPhone_1',
        'guardianMotherPhone_2',
        'guardianMotherPhone_3',
      ]
    },
    {
      title: '청구내용',
      fields: [
        'claimDate',
        'claimDateYear20NN',
        'claimDateYearNN',
        'claimDateYear',
        'claimDateYear2',
        'claimDateMonth',
        'claimDateDay',
        'claimType',
        'claimType_사망',
        'claimType_장해',
        'claimType_진단',
        'claimType_치과치료',
        'claimType_수술',
        'claimType_입원',
        'claimType_통원',
        'claimType_실손',
        'claimType_치료',
        'claimType_기타',
        'claimType_기타입력',
        'claimScope',
        'partialClaimReason',
        'claimCoverage',
        'claimCoverageExclusion',
        'claimAmount',
        'claimContent',
      ]
    },
    {
      title: '사고내용',
      fields: [
        'accidentDate',
        'accidentDateYear',
        'accidentDateMonth',
        'accidentDateDay',
        'accidentDateHour',
        'accidentDateMinute',
        'accidentType',
        'accidentType_질병',
        'accidentType_재해',
        'accidentType_상해',
        'accidentType_교통사고',
        'accidentType_자살',
        'accidentType_기타',
        'accidentLocation',
        'accidentDetails',
        'hospitalName',
        'diagnosis',
        'autoInsuranceProcessed',
        'autoInsuranceProcessed_Y',
        'autoInsuranceProcessed_N',
        'workersCompInsuranceProcessed',
        'workersCompInsuranceProcessed_Y',
        'workersCompInsuranceProcessed_N',
      ]
    },
    {
      title: '교통사고 관련',
      fields: [
        'vehicleNumber',
        'vehicleSeatPosition',
        'autoInsuranceClaimed',
        'autoInsuranceCompany',
        'autoInsuranceContact',
      ]
    },
    {
      title: '대행서비스',
      fields: [
        'proxyServiceRequested',
        'proxyServiceTarget',
      ]
    },
    {
      title: '펫보험',
      fields: [
        'petOwnerName',
        'petOwnerResidentNumber',
        'petOwnerResidentNumber_1',
        'petOwnerResidentNumber_2',
        'petOwnerPhone',
        'petOwnerPhone_1',
        'petOwnerPhone_2',
        'petOwnerPhone_3',
        'petOwnerAddress',
        'petName',
        'petBreed',
        'petGender',
        'petAge',
        'petBirthDate',
        'petBirthYear',
        'petBirthMonth',
        'petBirthDay',
        'petColor',
        'petReceiptType',
        'petDiagnosis',
        'petAccidentCauseDetail',
        'petNarrativeWho',
        'petNarrativeWhen',
        'petNarrativeWhere',
        'petNarrativeWhat',
        'petNarrativeHow',
        'petNarrativeWhy',
        'animalHospitalName',
        'animalHospitalPhone',
        'veterinarianName',
        'petTreatmentDateAndContent',
        'petClaimCoverage',
      ]
    },
    {
      title: '펫 배상책임',
      fields: [
        'petDamageVictimPhone',
        'petDamageVictimName',
        'petDamageVictimAddress',
        'petDamageDescription',
        'petDamageDetails',
        'petDamageVictimNameOrObject',
        'petClaimCoverageMulti',
        'petDamageEtc',
        'animalHospitalVetInfo',
        'petLossType',
        'accidentLocationCity',
        'accidentLocationDistrict',
        'accidentLocationTown',
        'liabilityAccidentTypeDetail',
        'accountHolderResidentNumber',
        'accountHolderResidentNumber_1',
        'accountHolderResidentNumber_2',
        'accountHolderRelation',
      ]
    },
    {
      title: '날짜/시간',
      fields: [
        'printedAt',
        'printedAtYear',
        'printedAtMonth',
        'printedAtDay',
      ]
    },
    {
      title: '기타',
      fields: [
        'claimSection',
        'claimItem',
        'attachedDocuments',
        'actorName',
        'actorPhone',
        'actorPhone_1',
        'actorPhone_2',
        'actorPhone_3',
        'loggedInUserName',
        'loggedInUserPhone',
        'loggedInUserPhone_1',
        'loggedInUserPhone_2',
        'loggedInUserPhone_3',
        'assignedPlannerLabel',
      ]
    },
  ]

  const fieldLabels = {
    customerName: '고객명',
    customerResidentNumber: '고객주민등록번호',
    customerResidentNumber_1: '고객주민등록번호 앞자리',
    customerResidentNumber_2: '고객주민등록번호 뒷자리',
    customerResidentNumber_rearDigit1: '고객 주민번호 뒷부분 첫 1자리(7번째)',
    customerResidentNumber_rearDigits2: '고객 주민번호 뒷부분 연속 2자리(8~9번째)',
    customerBirthDate: '고객생년월일',
    customerPhone: '고객연락처',
    customerPhone_1: '고객연락처 1',
    customerPhone_2: '고객연락처 2',
    customerPhone_3: '고객연락처 3',
    customerEmail: '고객 이메일',
    customerAddress: '고객주소',
    customerWorkplace: '고객 직장명',
    customerOccupation: '고객 하시는 일',
    customerSignature: '고객 서명(이미지·서명 완료 시)',
    contractHolderSameAsInsured: '계약자=피보험자 동일(Y/N)',
    contractHolderName: '계약자',
    contractHolderResidentNumber: '계약자주민등록번호',
    contractHolderResidentNumber_1: '계약자 주민등록번호 앞자리',
    contractHolderResidentNumber_2: '계약자 주민등록번호 뒷자리',
    contractHolderResidentNumber_rearDigit1: '계약자 주민번호 뒷부분 첫 1자리(7번째)',
    contractHolderResidentNumber_rearDigits2: '계약자 주민번호 뒷부분 연속 2자리(8~9번째)',
    contractHolderPhone: '계약자 연락처',
    contractHolderPhone_1: '계약자 연락처 1',
    contractHolderPhone_2: '계약자 연락처 2',
    contractHolderPhone_3: '계약자 연락처 3',
    contractHolderSignature: '계약자 서명(청구서 연결 이미지)',
    contractHolderSignatureImageBase64: '계약자 서명(이미지 data URL)',
    medicalBenefitRecipient: '의료 수급권자 해당여부',
    medicalBenefitRecipient_Y: '의료 수급권자-예',
    medicalBenefitRecipient_N: '의료 수급권자-아니오',
    drivingLicense: '운전여부',
    drivingLicense_Y: '운전여부-예',
    drivingLicense_N: '운전여부-아니오',
    beneficiaryName: '수익자 성명',
    beneficiaryRelationToInsured: '피보험자와의 관계',
    beneficiaryResidentNumber: '수익자 주민등록번호',
    beneficiaryResidentNo: '수익자 주민등록번호',
    beneficiaryResidentNumber_1: '수익자 주민등록번호 앞자리',
    beneficiaryResidentNumber_2: '수익자 주민등록번호 뒷자리',
    beneficiaryResidentNumber_rearDigit1: '수익자 주민번호 뒷부분 첫 1자리(7번째)',
    beneficiaryResidentNumber_rearDigits2: '수익자 주민번호 뒷부분 연속 2자리(8~9번째)',
    beneficiaryPhone: '수익자 휴대폰',
    beneficiaryPhone_1: '수익자 휴대폰 1',
    beneficiaryPhone_2: '수익자 휴대폰 2',
    beneficiaryPhone_3: '수익자 휴대폰 3',
    beneficiaryEmail: '수익자 이메일',
    beneficiaryAddress: '수익자 주소',
    beneficiaryWorkplace: '수익자 직장명',
    beneficiaryOccupation: '수익자 하시는 일',
    beneficiaryIdentityType: '수익자 신분증 정보 선택',
    beneficiaryResidentIdIssueDate: '수익자 주민등록증 발급일',
    beneficiaryResidentIdIssueDateYear: '수익자 주민등록증 발급일(년)',
    beneficiaryResidentIdIssueDateYear2: '수익자 주민등록증 발급일(년 2자리)',
    beneficiaryResidentIdIssueDateMonth: '수익자 주민등록증 발급일(월)',
    beneficiaryResidentIdIssueDateDay: '수익자 주민등록증 발급일(일)',
    beneficiaryResidentIdIssuer: '수익자 주민등록증 발급기관',
    beneficiaryDriverLicenseNumber: '수익자 운전면허번호',
    beneficiaryDriverLicenseNumberRegion: '수익자 운전면허번호 지역/앞자리',
    beneficiaryDriverLicenseNumberSecond: '수익자 운전면허번호 두 번째 칸',
    beneficiaryDriverLicenseNumberThird: '수익자 운전면허번호 세 번째 칸',
    beneficiaryDriverLicenseNumberFourth: '수익자 운전면허번호 네 번째 칸',
    beneficiaryDriverLicenseSerialNumber: '수익자 운전면허 암호일련번호',
    beneficiaryDriverLicenseIssueDate: '수익자 운전면허 발급일',
    beneficiaryDriverLicenseIssuer: '수익자 운전면허 발급기관',
    beneficiarySignature: '수익자 서명(청구서 연결 이미지)',
    benefitNotificationMethod: '보험금수령 안내방법',
    benefitNotificationMethodOption: '보험금수령안내방법 선택사항',
    benefitNotificationRecipient: '보상 안내 수신인 코드(PLANNER|CLAIMANT)',
    benefitGuidePlannerName: '보상안내 설계사 이름',
    benefitGuidePlannerEmail: '보상안내 설계사 이메일',
    benefitGuidePlannerResidentNumber: '보상안내 설계사 주민등록번호',
    benefitGuidePlannerResidentNumber_1: '보상안내 설계사 주민 앞자리',
    benefitGuidePlannerResidentNumber_2: '보상안내 설계사 주민 뒷자리',
    accidentType: '사고유형(통합 텍스트)',
    accidentType_질병: '사고유형·질병 선택 시 V',
    accidentType_재해: '사고유형·재해 선택 시 V',
    accidentType_상해: '사고유형·상해 선택 시 V',
    accidentType_교통사고: '사고유형·교통사고 선택 시 V',
    accidentType_자살: '사고유형·자살 선택 시 V',
    accidentType_기타: '사고유형·기타 선택 시 V',
    claimType: '청구유형(통합 텍스트)',
    claimType_사망: '청구유형·사망 선택 시 V',
    claimType_장해: '청구유형·장해 선택 시 V',
    claimType_진단: '청구유형·진단 선택 시 V',
    claimType_치과치료: '청구유형·치과치료 선택 시 V',
    claimType_수술: '청구유형·수술 선택 시 V',
    claimType_입원: '청구유형·입원 선택 시 V',
    claimType_통원: '청구유형·통원 선택 시 V',
    claimType_실손: '청구유형·실손 선택 시 V',
    claimType_치료: '청구유형·치료 선택 시 V',
    claimType_기타: '청구유형·기타(항목만/기타: 포함) 선택 시 V',
    claimType_기타입력: '청구유형·기타 입력란에 내용 있을 때만 V (기타:텍스트)',
    accidentLocation: '사고 장소',
    accidentDetails: '사고경위',
    hospitalName: '병원명',
    diagnosis: '진단명',
    vehicleNumber: '본인차량번호',
    vehicleSeatPosition: '본인 탑승위치',
    autoInsuranceClaimed: '자동차보험접수',
    autoInsuranceCompany: '자동차보험사',
    autoInsuranceContact: '자동차보험사 담당자/연락처',
    autoInsuranceProcessed: '자동차보험 처리 여부(예/아니오 값)',
    autoInsuranceProcessed_Y: '자동차보험 처리·예 선택 시 V',
    autoInsuranceProcessed_N: '자동차보험 처리·아니오 선택 시 V',
    workersCompInsuranceProcessed: '산재보험 처리 여부(예/아니오 값)',
    workersCompInsuranceProcessed_Y: '산재보험 처리·예 선택 시 V',
    workersCompInsuranceProcessed_N: '산재보험 처리·아니오 선택 시 V',
    claimContent: '청구내용',
    claimCoverage: '청구담보 선택',
    claimCoverageExclusion: '특정담보보상제외요청',
    claimScope: '청구 범위',
    partialClaimReason: '일부 청구 사유',
    accountPayoutType: '계좌 유형(AUTO_TRANSFER|PREPAID|GENERAL)',
    bankAutoTransfer: '자동이체계좌 여부(레거시·AUTO_TRANSFER 시 Y)',
    bankPrepaidAccount: '기지급계좌 여부(Y/N)',
    bankName: '보험금수령계좌 - 은행',
    accountNumber: '보험금수령계좌 - 계좌번호',
    accountHolder: '보험금수령계좌 - 예금주',
    proxyServiceRequested: '실손의료비 보험금 청구서류 접수대행 서비스 신청 여부',
    proxyServiceTarget: '청구서류 전송대상 회사',
    claimAmount: '청구금액',
    memo: '청구서 메모(청구 플로우에서 사고경위 등 통합)',
    claimDate: '청구일자',
    claimDateYear20NN: '청구일자(연 4자리·20NN)',
    claimDateYearNN: '청구일자(연 2자리·NN)',
    claimDateYear: '청구일자(연도 4자리·claimDateYear20NN 동일)',
    claimDateYear2: '청구일자(연도 2자리·claimDateYearNN 동일)',
    claimDateMonth: '청구일자(월)',
    claimDateDay: '청구일자(일)',
    accidentDate: '사고일자',
    accidentDateYear: '사고일자(년)',
    accidentDateYear2: '사고일자(연 2자리)',
    accidentDateMonth: '사고일자(월)',
    accidentDateDay: '사고일자(일)',
    accidentDateHour: '사고일자(시)',
    accidentDateMinute: '사고일자(분)',
    accidentReceiptType: '접수 구분(최초청구·추가접수)',
    claimSection: '청구섹션',
    claimItem: '청구항목',
    beneficiarySameAsCustomer: '수익자 동일 여부',
    beneficiarySameAsContractHolder: '수익자=계약자 동일(Y/N)',
    attachedDocuments: '자료첨부',
    defaultVCheck: 'V 체크',
    defaultSameAsAbove: '상동',
    printedAt: '출력일시',
    printedAtYear: '출력일시(년)',
    printedAtMonth: '출력일시(월)',
    printedAtDay: '출력일시(일)',
    actorName: '생성자명',
    actorPhone: '생성자 전화번호',
    actorPhone_1: '생성자 전화번호 1',
    actorPhone_2: '생성자 전화번호 2',
    actorPhone_3: '생성자 전화번호 3',
    loggedInUserName: '로그인 유저 이름',
    loggedInUserPhone: '로그인 유저 전화번호',
    loggedInUserPhone_1: '로그인 유저 전화번호 1',
    loggedInUserPhone_2: '로그인 유저 전화번호 2',
    loggedInUserPhone_3: '로그인 유저 전화번호 3',
    assignedPlannerLabel: '담당설계사',
    virtualFaxNumber: '가상 FAX',
    postalFaxNumber: '우체국 팩스',
    faxNumberSelection: 'FAX 번호 선택',
    injuryDiseaseTreatmentSupport: '상해질병치료지원금',
    groupActualMedicalInsurance: '단체실손보험',
    insuredJobDetail: '피보험자 직업 상세',
    identityType: '신분증 정보 선택',
    residentIdIssueDate: '주민등록증 발급일',
    residentIdIssueDateYear: '주민등록증 발급일(년)',
    residentIdIssueDateYear2: '주민등록증 발급일(년 2자리)',
    residentIdIssueDateMonth: '주민등록증 발급일(월)',
    residentIdIssueDateDay: '주민등록증 발급일(일)',
    residentIdIssuer: '주민등록증 발급기관',
    driverLicenseNumber: '운전면허번호',
    driverLicenseNumberRegion: '운전면허번호 지역',
    driverLicenseNumberSecond: '운전면허번호 두 번째 칸',
    driverLicenseNumberThird: '운전면허번호 세 번째 칸',
    driverLicenseNumberFourth: '운전면허번호 네 번째 칸',
    driverLicenseSerialNumber: '운전면허 일련번호',
    driverLicenseIssueDate: '운전면허 발급일',
    driverLicenseIssuer: '운전면허 발급기관',
    minorBeneficiary: '수익자 미성년자 여부',
    guardianSelection: '친권자 선택',
    guardianFatherName: '친권자 부 성명',
    guardianFatherResidentNumber: '친권자 부 주민등록번호',
    guardianFatherResidentNumber_1: '친권자 부 주민등록번호 앞자리',
    guardianFatherResidentNumber_2: '친권자 부 주민등록번호 뒷자리',
    guardianFatherPhone: '친권자 부 연락처',
    guardianFatherPhone_1: '친권자 부 연락처 1',
    guardianFatherPhone_2: '친권자 부 연락처 2',
    guardianFatherPhone_3: '친권자 부 연락처 3',
    guardianMotherName: '친권자 모 성명',
    guardianMotherResidentNumber: '친권자 모 주민등록번호',
    guardianMotherResidentNumber_1: '친권자 모 주민등록번호 앞자리',
    guardianMotherResidentNumber_2: '친권자 모 주민등록번호 뒷자리',
    guardianMotherPhone: '친권자 모 연락처',
    guardianMotherPhone_1: '친권자 모 연락처 1',
    guardianMotherPhone_2: '친권자 모 연락처 2',
    guardianMotherPhone_3: '친권자 모 연락처 3',
    petOwnerName: '반려동물 보호자 성명',
    petOwnerResidentNumber: '반려동물 보호자 주민등록번호',
    petOwnerResidentNumber_1: '반려동물 보호자 주민등록번호 앞자리',
    petOwnerResidentNumber_2: '반려동물 보호자 주민등록번호 뒷자리',
    petOwnerPhone: '반려동물 보호자 연락처',
    petOwnerPhone_1: '반려동물 보호자 연락처 1',
    petOwnerPhone_2: '반려동물 보호자 연락처 2',
    petOwnerPhone_3: '반려동물 보호자 연락처 3',
    petOwnerAddress: '반려동물 보호자 주소',
    petName: '반려동물 이름',
    petBreed: '반려동물 품종',
    petGender: '반려동물 성별',
    petAge: '반려동물 나이',
    petBirthDate: '반려동물 생년월일',
    petBirthYear: '반려동물 출생년도',
    petBirthMonth: '반려동물 출생월',
    petBirthDay: '반려동물 출생일',
    petColor: '반려동물 모색',
    petReceiptType: '펫보험 접수 구분',
    petDiagnosis: '반려동물 진단명',
    petAccidentCauseDetail: '반려동물 사고 원인 상세',
    petNarrativeWho: '사고 경위 - 누가',
    petNarrativeWhen: '사고 경위 - 언제',
    petNarrativeWhere: '사고 경위 - 어디서',
    petNarrativeWhat: '사고 경위 - 무엇을',
    petNarrativeHow: '사고 경위 - 어떻게',
    petNarrativeWhy: '사고 경위 - 왜',
    animalHospitalName: '동물병원 이름',
    animalHospitalPhone: '동물병원/수의사 전화번호',
    veterinarianName: '수의사 이름',
    petDamageVictimPhone: '피해자 연락처',
    petDamageVictimName: '피해자 성명',
    petDamageVictimAddress: '피해자 주소',
    petDamageDescription: '손해 내용',
    petDamageDetails: '피해내역 및 물품',
    petDamageVictimNameOrObject: '피해자명 또는 피해물명',
    petClaimCoverageMulti: '청구 담보',
    petDamageEtc: '기타 손해 내용',
    animalHospitalVetInfo: '동물병원/수의사 정보',
    petTreatmentDateAndContent: '치료 일자 및 내용',
    petClaimCoverage: '펫보험 청구 담보 선택',
    petLossType: '손해 유형',
    accidentLocationCity: '사고 장소 - 시',
    accidentLocationDistrict: '사고 장소 - 구/군',
    accidentLocationTown: '사고 장소 - 읍/면/동/리',
    liabilityAccidentTypeDetail: '배상책임 사고유형 상세',
    accountHolderResidentNumber: '예금주 주민등록번호',
    accountHolderResidentNumber_1: '예금주 주민등록번호 앞자리',
    accountHolderResidentNumber_2: '예금주 주민등록번호 뒷자리',
    accountHolderRelation: '예금주와의 관계',
  }

  const additionalFieldsPanel = (
    <div className="rounded border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          className="flex min-w-0 items-center gap-2 rounded px-1 py-1 text-left hover:bg-slate-50"
          onClick={() => setAdditionalFieldsOpen((prev) => !prev)}
          aria-expanded={additionalFieldsOpen}
        >
          {additionalFieldsOpen ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden="true" />
          )}
          <span className="text-sm font-semibold text-slate-900">추가 입력 항목</span>
          <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
            {additionalFieldsLoading ? '불러오는 중' : `${additionalFields.length}개`}
          </span>
        </button>
        {additionalFieldsOpen && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50"
              onClick={addAdditionalField}
              disabled={additionalFieldsLoading || additionalFieldsSaving}
            >
              항목 추가
            </button>
            <button
              type="button"
              className="rounded bg-brand-navy px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              onClick={saveAdditionalFields}
              disabled={additionalFieldsLoading || additionalFieldsSaving}
            >
              {additionalFieldsSaving ? '저장 중...' : '저장'}
            </button>
          </div>
        )}
      </div>
      {additionalFieldsOpen && <div className="mt-3 overflow-x-auto rounded border">
        <table className="w-full min-w-[980px] text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr className="text-left">
              <th className="px-2 py-2 w-[8rem]">필드 키</th>
              <th className="px-2 py-2 w-[12rem]">항목명</th>
              <th className="px-2 py-2 w-[8rem]">입력 유형</th>
              <th className="px-2 py-2 w-[6rem] text-center">필수</th>
              <th className="px-2 py-2 w-[6rem] text-center">민감값</th>
              <th className="px-2 py-2 w-[6rem] text-center">활성</th>
              <th className="px-2 py-2 w-[5rem]">순서</th>
              <th className="px-2 py-2">선택지</th>
              <th className="px-2 py-2 w-[4rem]"></th>
            </tr>
          </thead>
          <tbody>
            {additionalFields.length === 0 && (
              <tr>
                <td colSpan={9} className="px-2 py-5 text-center text-slate-500">
                  {additionalFieldsLoading ? '불러오는 중...' : '설정된 추가 입력 항목이 없습니다.'}
                </td>
              </tr>
            )}
            {additionalFields.map((row, idx) => (
              <tr key={`${row.fieldKey || 'new'}_${idx}`} className="border-t align-top">
                <td className="px-2 py-2">
                  <input
                    className="w-full rounded border px-2 py-1.5 text-sm"
                    value={row.fieldKey}
                    onChange={(e) => updateAdditionalField(idx, { fieldKey: e.target.value })}
                    placeholder="driverLicenseNo"
                  />
                </td>
                <td className="px-2 py-2">
                  <input
                    className="w-full rounded border px-2 py-1.5 text-sm"
                    value={row.label}
                    onChange={(e) => updateAdditionalField(idx, { label: e.target.value })}
                    placeholder="운전면허번호"
                  />
                </td>
                <td className="px-2 py-2">
                  <select
                    className="w-full rounded border px-2 py-1.5 text-sm"
                    value={row.inputType}
                    onChange={(e) => updateAdditionalField(idx, { inputType: e.target.value })}
                  >
                    {CLAIM_ADDITIONAL_FIELD_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </td>
                {['required', 'sensitive', 'active'].map((key) => (
                  <td key={key} className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      className="mt-2"
                      checked={!!row[key]}
                      onChange={(e) => updateAdditionalField(idx, { [key]: e.target.checked })}
                    />
                  </td>
                ))}
                <td className="px-2 py-2">
                  <input
                    type="number"
                    className="w-full rounded border px-2 py-1.5 text-sm"
                    value={row.sortOrder}
                    onChange={(e) => updateAdditionalField(idx, { sortOrder: Number(e.target.value) })}
                  />
                </td>
                <td className="px-2 py-2">
                  {isChoiceAdditionalFieldType(row.inputType) ? (
                    <textarea
                      className="min-h-[4.5rem] w-full rounded border px-2 py-1.5 text-sm"
                      value={row.optionsText || ''}
                      onChange={(e) => updateAdditionalField(idx, { optionsText: e.target.value })}
                      placeholder={'예\nY|예\nN|아니오'}
                    />
                  ) : (
                    <div className="px-2 py-1.5 text-xs text-slate-400">-</div>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  <button
                    type="button"
                    className="rounded border border-rose-200 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                    onClick={() => removeAdditionalField(idx)}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}
    </div>
  )

  return (
    <div className="space-y-4">
      {!embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">청구서식</h1>
            <MenuGuideButton slug="claims" title="보험금청구 가이드" />
          </div>
          <div className="flex flex-wrap gap-2 text-sm">
            <Link className="rounded border px-3 py-1.5 hover:bg-slate-50" to="/ops/forms/claims/fax">청구번호</Link>
            <Link className="rounded border px-3 py-1.5 hover:bg-slate-50" to="/ops/stats/claims">청구통계</Link>
            <Link className="rounded border px-3 py-1.5 hover:bg-slate-50" to="/support/claims">보험금 청구</Link>
            <Link className="rounded border px-3 py-1.5 hover:bg-slate-50" to="/support/claims/applications">청구 이력</Link>
          </div>
        </div>
      ) : null}
      
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <h2 className="text-xl font-bold">청구서 템플릿 설정</h2>
        <div className="flex flex-col gap-2 items-stretch sm:items-end min-w-[280px]">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              className="rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
              onClick={downloadMappingsJsonForSelectedInsurer}
              disabled={mappingBusy || !selectedInsurerOrgId}
              title={!selectedInsurerOrgId ? '목록에서 보험사를 선택하세요' : '선택한 보험사 1곳의 좌표만 저장합니다'}
            >
              선택 보험사 좌표 다운로드
            </button>
            <label className={`rounded border px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer ${mappingBusy ? 'opacity-60 pointer-events-none' : ''}`}>
              좌표 JSON 업로드
              <input type="file" accept="application/json" className="hidden" onChange={importMappingsJson} />
            </label>
          </div>
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={mappingExportIncludeFileId}
              onChange={(e) => setMappingExportIncludeFileId(e.target.checked)}
            />
            <span>
              다운로드에 <span className="font-medium">templateFileId 포함</span> (로컬·운영 DB/파일 ID가 같을 때만)
            </span>
          </label>
          <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={mappingImportCrossEnvMode}
              onChange={(e) => setMappingImportCrossEnvMode(e.target.checked)}
            />
            <span>
              업로드 시 <span className="font-medium">선택 보험사에만 적용</span>·가져온 fileId 무시 (운영 등 다른 환경)
            </span>
          </label>
          <button
            type="button"
            className="text-xs text-slate-500 underline hover:text-slate-800 text-left"
            onClick={downloadAllMappingsBackup}
            disabled={mappingBusy}
          >
            전체 보험사 일괄 백업 다운로드 (fileId·조직 ID 포함, 동일 환경용)
          </button>
        </div>
      </div>

      <div className="rounded border bg-white p-4">
        <div className="grid gap-4 xl:grid-cols-[minmax(360px,0.8fr)_minmax(560px,1.2fr)]">
        <div className="rounded border border-slate-200 overflow-hidden bg-white">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 bg-slate-50 px-3 py-2.5 text-left hover:bg-slate-100 border-b border-slate-200"
            aria-expanded={insurerPickerOpen}
            onClick={() => setInsurerPickerOpen((v) => !v)}
          >
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">보험사 선택 · 서식 현황</div>
              <div className="text-xs text-slate-500 mt-0.5 truncate">
                {selectedInsurer
                  ? `${selectedInsurer.name} · ${claimInsurerCategoryLabel(selectedInsurer, lifeRootId, nonlifeRootId, petRootId, liabilityRootId)}`
                  : '목록에서 보험사를 선택하세요'}
              </div>
            </div>
            <span className="shrink-0 text-xs font-medium text-slate-600 tabular-nums">
              {insurerPickerOpen ? '접기 ▲' : '펼치기 ▼'}
            </span>
          </button>

          {insurerPickerOpen && (
            <div className="p-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-700">구분</span>
                {CLAIM_INSURER_CATEGORY_FILTERS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={`rounded border px-3 py-1.5 text-sm ${
                      categoryFilter === key ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' : 'bg-white hover:bg-slate-50'
                    }`}
                    onClick={() => setCategoryFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {insurersLoading && <div className="text-sm text-slate-500">보험사 목록을 불러오는 중…</div>}

              <div className="rounded border overflow-hidden">
                <div className="max-h-[min(320px,40vh)] overflow-auto">
                  <table className="w-full text-sm min-w-[820px]">
                    <thead className="sticky top-0 bg-slate-50 border-b z-10">
                      <tr className="text-left text-xs text-slate-600">
                        <th className="px-2 py-2 w-[5rem]">로고</th>
                        <th className="px-2 py-2">보험사</th>
                        <th className="px-2 py-2 w-12">구분</th>
                        <th className="px-2 py-2 w-14">서식</th>
                        <th className="px-2 py-2 w-[5.5rem] text-center">청구활성</th>
                        <th className="px-2 py-2 w-[4.5rem]">버전</th>
                        <th className="px-2 py-2 min-w-[7rem]">최종수정</th>
                        <th className="px-2 py-2 w-[5.5rem] text-center">좌표</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInsurers.length === 0 && !insurersLoading && (
                        <tr>
                          <td colSpan={8} className="px-2 py-6 text-center text-slate-500">
                            해당 구분에 표시할 보험사가 없습니다.
                          </td>
                        </tr>
                      )}
                      {filteredInsurers.map((org) => {
                        const st = templateStatusById[String(org.id)]
                        const sel = String(selectedInsurerOrgId) === String(org.id)
                        return (
                          <tr
                            key={org.id}
                            role="button"
                            tabIndex={0}
                            className={`border-b last:border-b-0 cursor-pointer ${sel ? 'bg-sky-50' : 'hover:bg-slate-50'}`}
                            onClick={() => setSelectedInsurerOrgId(org.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                setSelectedInsurerOrgId(org.id)
                              }
                            }}
                          >
                            <td className="px-2 py-2 align-top">
                              {(() => {
                                const logo = getInsurerLogoSrc(org)
                                return logo ? (
                                  <img
                                    src={logo}
                                    alt=""
                                    className="h-9 max-h-9 w-[4.5rem] max-w-[4.5rem] object-contain rounded border border-slate-200 bg-white"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div
                                    className="flex h-9 w-[4.5rem] items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 text-[10px] text-slate-400"
                                    title="로고 없음"
                                  >
                                    —
                                  </div>
                                )
                              })()}
                            </td>
                            <td className="px-2 py-2 font-medium text-slate-900 align-top">{org.name}</td>
                            <td className="px-2 py-2 text-slate-600 align-top">
                              {claimInsurerCategoryLabel(org, lifeRootId, nonlifeRootId, petRootId, liabilityRootId)}
                            </td>
                            <td className="px-2 py-2 align-top">
                              {st?.hasTemplate
                                ? <span className="text-emerald-700 font-medium">있음</span>
                                : <span className="text-slate-400">없음</span>}
                            </td>
                            <td className="px-2 py-2 align-top text-center">
                              <button
                                type="button"
                                className={`rounded border px-2 py-0.5 text-[11px] font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
                                  st?.claimEnabled
                                    ? 'border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100'
                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                                }`}
                                disabled={!st?.hasTemplate || claimToggleBusyOrgId != null}
                                title={
                                  !st?.hasTemplate
                                    ? '템플릿 저장 후 청구 활성화할 수 있습니다.'
                                    : st?.claimEnabled
                                      ? '사용자 보험금청구 STEP1에서 선택 가능합니다.'
                                      : '사용자 보험금청구 STEP1에서 선택되지 않습니다.'
                                }
                                onClick={(ev) => toggleClaimEnabledForInsurer(org, !st?.claimEnabled, ev)}
                              >
                                {claimToggleBusyOrgId === org.id ? '변경 중' : st?.claimEnabled ? '활성' : '비활성'}
                              </button>
                            </td>
                            <td className="px-2 py-2 text-slate-700 align-top truncate max-w-[4.5rem]" title={st?.formVersion || ''}>
                              {st?.hasTemplate && st?.formVersion ? st.formVersion : '-'}
                            </td>
                            <td className="px-2 py-2 text-xs text-slate-600 align-top whitespace-nowrap">
                              {st?.hasTemplate ? formatTemplateUpdatedAt(st.updatedAt) : '-'}
                            </td>
                            <td className="px-1 py-1.5 align-top text-center">
                              <button
                                type="button"
                                className="rounded border border-rose-200 bg-white px-1.5 py-0.5 text-[11px] font-medium text-rose-800 hover:bg-rose-50 disabled:opacity-40 disabled:pointer-events-none"
                                title="필드 좌표만 비웁니다. PDF는 유지됩니다."
                                disabled={!st?.hasTemplate || resettingCoordinatesOrgId != null}
                                onClick={(ev) => resetCoordinatesForInsurer(org, ev)}
                              >
                                {resettingCoordinatesOrgId === org.id ? '…' : '초기화'}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="contents">
            {!selectedInsurerOrgId && (
              <div className="rounded border border-dashed bg-slate-50 p-8 text-center text-sm text-slate-600">
                위에서 보험사를 선택하면 템플릿을 편집할 수 있습니다. 좌표 작업 시 상단 패널을 접으면 PDF 영역이 넓어집니다.
              </div>
            )}

            {selectedInsurerOrgId && (
              <>
                <div className="contents">
                <div className="space-y-4 rounded border border-slate-200 bg-slate-50/40 p-3">
                    <p className="text-sm font-semibold text-slate-900">기본 설정</p>
            <div>
              <label className="text-sm text-slate-600 mb-1 block">양식 버전 (선택)</label>
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={formVersion}
                onChange={(e) => setFormVersion(e.target.value)}
                placeholder="예: v1.0"
              />
            </div>

            <div>
              <label className="text-sm text-slate-600 mb-1 block">PDF 기본 글꼴 (선택)</label>
              <select
                className="w-full max-w-md rounded border px-3 py-2 text-sm"
                value={defaultPdfFontKey}
                onChange={(e) => setDefaultPdfFontKey(e.target.value)}
              >
                {PDF_MAPPING_FONT_OPTIONS.map((opt) => (
                  <option key={opt.value || 'auto'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-xs text-slate-500">
                필드마다「PDF 폰트」를 비우면 이 설정이 적용됩니다. 자동이면 서버·OS에 설치된 기본 한글 폰트를 씁니다.
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-600 mb-1 block">팩스 발송 페이지 (선택)</label>
              <input
                className="w-full rounded border px-3 py-2 text-sm"
                value={faxPageRangesText}
                onChange={(e) => setFaxPageRangesText(e.target.value)}
                placeholder="예: 1-2,5 (비우면 전체 페이지)"
              />
              <div className="mt-1 text-xs text-slate-500">
                비워두면 전체 페이지가 발송됩니다.
              </div>
            </div>

            <div>
              <label className="text-sm text-slate-600 mb-1 block">템플릿 PDF *</label>
              <input
                type="file"
                accept="application/pdf"
                className="w-full rounded border px-3 py-2 text-sm"
                onChange={handleTemplateUpload}
                disabled={uploadingTemplate}
              />
              {templateFileId && (
                <div className="mt-2 text-xs text-slate-600">
                  업로드됨: {templateFileId}
                  <button
                    type="button"
                    className="ml-2 text-blue-600 hover:underline"
                    onClick={() => window.open(filePreviewRelativeUrl(templateFileId), '_blank', 'noopener,noreferrer')}
                  >
                    미리보기
                  </button>
                  <button
                    type="button"
                    className="ml-2 text-blue-600 hover:underline"
                    onClick={downloadTemplateFile}
                  >
                    다운로드
                  </button>
                </div>
              )}
            </div>
                </div>
                <div className="contents">
                    {!templateFileId ? (
                      <div className="rounded border border-amber-200 bg-amber-50 px-4 py-8 text-center text-sm text-amber-950">
                        먼저 위에서 템플릿 PDF를 업로드한 뒤 필드를 선택하고 좌표를 지정할 수 있습니다.
                      </div>
                    ) : (
                      <>
                <div className="rounded border p-3 bg-slate-50">
                  <div className="text-sm font-medium mb-3">필드 선택</div>
                  <div className="mb-3">
                    {additionalFieldsPanel}
                  </div>
                  <p className="mb-3 rounded border border-cyan-200 bg-cyan-50/80 px-2 py-1.5 text-xs text-cyan-950 leading-relaxed">
                    <span className="font-semibold text-cyan-900">청록 배경</span>은 보험금 청구 화면에서 <span className="font-medium">직접 입력·선택</span>하는 항목(및 청구일·사고일 파생·서명)과 연결되는 bindKey입니다.
                    API에만 자동으로 넣는 고정·숨김 필드는 제외했습니다. PDF 위 박스도 동일 기준으로 청록 테두리입니다.
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b border-slate-200">
                    <button
                      type="button"
                      className="rounded border border-emerald-600 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
                      onClick={addDefaultCheckVField}
                    >
                      V 체크
                    </button>
                    <button
                      type="button"
                      className="rounded border border-emerald-600 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
                      onClick={addDefaultSameAsAboveField}
                    >
                      상동
                    </button>
                    <span className="text-xs text-slate-600 max-w-xl">
                      청구 입력값과 관계없이 해당 좌표에 항상 V 또는 상동이 출력됩니다. 아래 PDF에서 칸 위치만 맞추면 됩니다.
                    </span>
                  </div>
                  <div className="space-y-2">
                    {fieldSections.map((section) => {
                      const isExpanded = activeFieldSectionKey === section.title
                      return (
                        <div key={section.title} className="border-b border-slate-200 last:border-b-0 pb-2 last:pb-0">
                          <button
                            type="button"
                            className="w-full flex items-center justify-between text-xs font-semibold text-slate-600 mb-2 hover:text-slate-900"
                            onClick={() => setActiveFieldSectionKey(section.title)}
                          >
                            <span>{section.title}</span>
                            <span className="text-slate-400">
                              {isExpanded ? '▼' : '▶'}
                            </span>
                          </button>
                          {isExpanded && (
                            <div className="flex flex-wrap gap-2">
                              {section.fields.map((k) => {
                                const isSelected = fields.some(f => f.bindKey === k && f.key === selectedFieldKey)
                                const flowHL = isClaimFlowCollectedBindKey(k)
                                const label = fieldLabels[k] || k
                                return (
                                  <button
                                    key={k}
                                    type="button"
                                    className={`rounded border px-3 py-1 text-sm max-w-[200px] ${
                                      isSelected
                                        ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10' 
                                        : flowHL
                                          ? 'border-cyan-600 bg-cyan-50 text-cyan-950 hover:bg-cyan-100'
                                          : 'bg-white hover:bg-slate-50'
                                    }`}
                                    onClick={() => {
                                      ensureField(k)
                                    }}
                                    title={`${label} (${k})`}
                                  >
                                    <span className="block truncate" title={label}>
                                      {label}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                    <div className="border-t border-slate-300 pt-3 mt-2">
                      <div className="pb-2">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between text-xs font-semibold text-slate-800 mb-2 hover:text-slate-900"
                          onClick={() => setActiveFieldSectionKey(claimFlowDirectInputSectionTitle)}
                        >
                          <span>{claimFlowDirectInputSectionTitle}</span>
                          <span className="text-slate-400">
                            {activeFieldSectionKey === claimFlowDirectInputSectionTitle ? '▼' : '▶'}
                          </span>
                        </button>
                        {activeFieldSectionKey === claimFlowDirectInputSectionTitle && (
                          <>
                            <p className="mb-2 text-xs text-slate-600 leading-relaxed">
                              지원 &gt; 보험금 청구 화면에서 <span className="font-medium">직접 입력·선택</span>하는 bindKey와 청구·사고일 파생·서명만 모았습니다.
                              위 섹션과 겹치더라도 한곳에서 고를 수 있습니다.
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {CLAIM_FLOW_COLLECTED_BIND_KEYS.map((k) => {
                                const isSelected = fields.some((f) => f.bindKey === k && f.key === selectedFieldKey)
                                const flowHL = isClaimFlowCollectedBindKey(k)
                                const label = fieldLabels[k] || k
                                return (
                                  <button
                                    key={`manual-${k}`}
                                    type="button"
                                    className={`rounded border px-3 py-1 text-sm max-w-[200px] ${
                                      isSelected
                                        ? 'border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                                        : flowHL
                                          ? 'border-cyan-600 bg-cyan-50 text-cyan-950 hover:bg-cyan-100'
                                          : 'bg-white hover:bg-slate-50'
                                    }`}
                                    onClick={() => {
                                      ensureField(k)
                                    }}
                                    title={`${label} (${k})`}
                                  >
                                    <span className="block truncate" title={label}>
                                      {label}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    PDF 위에서 드래그하면 <span className="font-medium">
                      {selectedFieldKey 
                        ? (() => {
                            const field = fields.find(f => f.key === selectedFieldKey)
                            return field ? (fieldLabels[field.bindKey] || field.bindKey) : '필드를 선택하세요'
                          })()
                        : '필드를 선택하세요'}
                    </span> 필드에 좌표가 들어갑니다.
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="rounded border border-emerald-600 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
                      onClick={addDefaultCheckVField}
                      title="상단 V 체크와 동일합니다. 항상 V가 찍히는 필드를 하나 추가합니다."
                    >
                      V 체크
                    </button>
                    <span className="text-xs text-slate-600">
                      상단과 동일 — 스크롤 없이 여기서도 추가할 수 있습니다.
                    </span>
                    <button
                      type="button"
                      className="rounded border border-emerald-600 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-900 hover:bg-emerald-100"
                      onClick={addDefaultSameAsAboveField}
                      title="항상 '상동'이 찍히는 필드를 하나 추가합니다."
                    >
                      상동
                    </button>
                  </div>
                </div>
                <div className="rounded border p-3 bg-white">
                  <p className="text-sm font-medium text-slate-800 mb-3">PDF 미리보기</p>
                  <div className="flex flex-col gap-2 mb-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-4">
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300"
                          checked={showPdfMappingFieldLabels}
                          onChange={(e) => setShowPdfMappingFieldLabels(e.target.checked)}
                        />
                        <span>PDF에 매핑 필드명 표시</span>
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300"
                          checked={pdfCoordinatePreviewEnabled}
                          onChange={(e) => setPdfCoordinatePreviewEnabled(e.target.checked)}
                        />
                        <span>좌표에 예상 입력값 미리보기</span>
                      </label>
                    </div>
                    <span className="text-xs text-slate-500 max-w-xl">
                      끄면 양식 위 라벨 배지만 숨기고, 박스와 삭제는 그대로입니다. 미리보기는 샘플 문구·로그인 설계사명이며 실제 청구 고객 PDF와 다를 수 있습니다.
                    </span>
                  </div>
                  <PdfTemplateMapper
                    fileId={templateFileId}
                    fields={fields}
                    selectedKey={selectedFieldKey}
                    onSelectKey={(k) => {
                      // k는 이미 존재하는 필드의 고유 key이므로 ensureField 호출하지 않음
                      setSelectedFieldKey(k)
                    }}
                    onSetBox={(k, box) => {
                      // k는 고유 key이므로 직접 업데이트
                      updateField(k, { ...box })
                    }}
                    onSetBoxes={updateFields}
                    onQuickCheckAdd={addQuickVCheckField}
                    enableBulkTools
                    previewEnabled={pdfCoordinatePreviewEnabled}
                    previewValues={claimPreviewValues}
                    onPageIndexChange={(idx) => {
                      if (typeof idx === 'number' && !Number.isNaN(idx)) {
                        setPdfPageIndex(Math.max(0, idx))
                      }
                    }}
                    fieldLabels={fieldLabels}
                    claimFlowHighlightBindKeys={claimFlowBindKeySet}
                    showMappingOverlayLabels={showPdfMappingFieldLabels}
                    templateDefaultFontKey={defaultPdfFontKey}
                    onDeleteField={(key) => {
                      setFields(fields.filter(f => f.key !== key))
                      if (selectedFieldKey === key) {
                        setSelectedFieldKey(null)
                      }
                    }}
                  />
                </div>
                      </>
                    )}
                  </div>

                <div className="space-y-4 xl:col-span-2 w-full min-w-0">
                    <p className="text-sm font-semibold text-slate-900">필드 표 및 저장</p>
                    {!templateFileId ? (
                      <div className="rounded border border-amber-200 bg-amber-50 px-4 py-8 text-center text-sm text-amber-950">
                        먼저 위에서 템플릿 PDF를 업로드한 뒤 필드 표를 편집할 수 있습니다.
                      </div>
                    ) : (
                      <>
                <div className="rounded border p-3 bg-slate-50">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-2 text-left text-sm font-medium text-slate-800 rounded-md py-1.5 px-1 -mx-1 hover:bg-slate-100/90"
                    onClick={() => setFieldEditorOpen((v) => !v)}
                    aria-expanded={fieldEditorOpen}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      필드 편집
                      <span className="text-xs font-normal text-slate-500 truncate">
                        {hideVCheckInFieldEditor && vCheckFieldCount > 0
                          ? `(${fieldEditorRows.length}개 · V ${vCheckFieldCount}개 제외)`
                          : `(${fields.length}개)`}
                      </span>
                    </span>
                    <span className="text-slate-400 shrink-0 text-xs tabular-nums" aria-hidden>
                      {fieldEditorOpen ? '▼ 접기' : '▶ 펼치기'}
                    </span>
                  </button>
                  {fieldEditorOpen && (
                  <>
                  <label className="mt-2 flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300"
                      checked={hideVCheckInFieldEditor}
                      onChange={(e) => setHideVCheckInFieldEditor(e.target.checked)}
                    />
                    <span>V 체크 제외하고 표시</span>
                  </label>
                  <div className="overflow-x-auto mt-2">
                    <table className="min-w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-2 py-1 text-left">key (고유ID)</th>
                          <th className="px-2 py-1 text-left">page</th>
                          <th className="px-2 py-1 text-left">x</th>
                          <th className="px-2 py-1 text-left">y</th>
                          <th className="px-2 py-1 text-left">w</th>
                          <th className="px-2 py-1 text-left">h</th>
                          <th className="px-2 py-1 text-left">크기(pt)</th>
                          <th className="px-2 py-1 text-left" title="PDF 출력 시 글꼴 파일. 서버(OS/Docker)에 해당 폰트가 있어야 적용됩니다.">
                            PDF 폰트
                          </th>
                          <th className="px-2 py-1 text-left">align</th>
                          <th className="px-2 py-1 text-left text-cyan-900" title="보험금 청구 플로우와 연결되는 항목은 행·열 배경이 청록색입니다">
                            bindKey (필드명)
                          </th>
                          <th className="px-2 py-1 text-left">format</th>
                          <th className="px-2 py-1 text-left">valueType</th>
                          <th className="px-2 py-1 text-left"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {fieldEditorRows.length === 0 ? (
                          <tr>
                            <td colSpan={13} className="px-2 py-4 text-center text-slate-500">
                              표시할 필드가 없습니다.
                            </td>
                          </tr>
                        ) : null}
                        {fieldEditorRows.map(({ f, idx }) => {
                          const rowFlow = isClaimFlowCollectedBindKey(f.bindKey)
                          return (
                          <tr key={f.key || idx} className={`border-t ${rowFlow ? 'bg-cyan-50/40' : ''}`}>
                            <td className="px-2 py-1">
                              <div className="text-xs text-slate-600 truncate max-w-[120px]" title={f.key || ''}>
                                {f.key || '-'}
                              </div>
                              <div 
                                className="text-xs text-slate-400 mt-0.5 truncate max-w-[120px]" 
                                title={f.bindKey ? `${fieldLabels[f.bindKey] || f.bindKey} (${f.bindKey})` : ''}
                              >
                                {f.bindKey ? (fieldLabels[f.bindKey] || f.bindKey) : '-'}
                              </div>
                            </td>
                            <td className="px-2 py-1">
                              <input
                                className="w-12 rounded border px-2 py-1"
                                type="number"
                                value={f.pageIndex || 0}
                                onChange={(e) => {
                                  const newFields = [...fields]
                                  newFields[idx] = { ...f, pageIndex: Number(e.target.value) }
                                  setFields(newFields)
                                }}
                              />
                            </td>
                            {['x', 'y', 'w', 'h'].map((k) => (
                              <td key={k} className="px-2 py-1">
                                <input
                                  className="w-16 rounded border px-2 py-1"
                                  type="number"
                                  step="0.001"
                                  value={f[k] || 0}
                                  onChange={(e) => {
                                    const newFields = [...fields]
                                    newFields[idx] = { ...f, [k]: Number(e.target.value) }
                                    setFields(newFields)
                                  }}
                                />
                              </td>
                            ))}
                            <td className="px-2 py-1">
                              <input
                                className="w-14 rounded border px-2 py-1"
                                type="number"
                                value={f.fontSize || 12}
                                onChange={(e) => {
                                  const newFields = [...fields]
                                  newFields[idx] = { ...f, fontSize: Number(e.target.value) }
                                  setFields(newFields)
                                }}
                              />
                            </td>
                            <td className="px-2 py-1">
                              <select
                                className="max-w-[9rem] rounded border px-1 py-1 text-[11px]"
                                value={f.fontKey || ''}
                                onChange={(e) => {
                                  const newFields = [...fields]
                                  newFields[idx] = { ...f, fontKey: e.target.value }
                                  setFields(newFields)
                                }}
                              >
                                {PDF_MAPPING_FONT_OPTIONS.map((opt) => (
                                  <option key={opt.value || 'auto'} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <select
                                className="w-20 rounded border px-2 py-1"
                                value={f.align || 'left'}
                                onChange={(e) => {
                                  const newFields = [...fields]
                                  newFields[idx] = { ...f, align: e.target.value }
                                  setFields(newFields)
                                }}
                              >
                                <option value="left">left</option>
                                <option value="center">center</option>
                                <option value="right">right</option>
                              </select>
                            </td>
                            <td className={`px-2 py-1 ${rowFlow ? 'bg-cyan-50/60' : ''}`}>
                              <input
                                className={`w-32 rounded border px-2 py-1 ${rowFlow ? 'border-cyan-500' : ''}`}
                                value={f.bindKey || ''}
                                onChange={(e) => {
                                  const newFields = [...fields]
                                  newFields[idx] = { ...f, bindKey: e.target.value }
                                  setFields(newFields)
                                }}
                                placeholder="필드명"
                              />
                              {f.bindKey && (
                                <div 
                                  className={`text-xs mt-0.5 truncate max-w-[120px] ${rowFlow ? 'text-cyan-900 font-medium' : 'text-slate-500'}`}
                                  title={fieldLabels[f.bindKey] || f.bindKey}
                                >
                                  {fieldLabels[f.bindKey] || '-'}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-1">
                              <select
                                className="w-28 rounded border px-2 py-1"
                                value={f.format || ''}
                                onChange={(e) => {
                                  const newFields = [...fields]
                                  newFields[idx] = { ...f, format: e.target.value || '' }
                                  setFields(newFields)
                                }}
                              >
                                <option value="">없음</option>
                                <option value="phone">전화번호 (010-1234-5678)</option>
                                <option value="residentNumber">주민등록번호 (123456-1234567)</option>
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <select
                                className="w-32 rounded border px-2 py-1"
                                value={f.valueType || ''}
                                onChange={(e) => {
                                  const newFields = [...fields]
                                  newFields[idx] = { ...f, valueType: e.target.value || '' }
                                  setFields(newFields)
                                }}
                              >
                                <option value="">일반 텍스트</option>
                                <option value="DIGITS_SPREAD">숫자 균등 배치 (한 칸에 자리마다 간격)</option>
                                <option value="CHECK_V">기본 V (입력 무관·항상 표시)</option>
                                <option value="SAME_AS_ABOVE">상동 (입력 무관·항상 표시)</option>
                                <option value="CHECK_YES">예/아니오 - 예만 체크</option>
                                <option value="CHECK_NO">예/아니오 - 아니오만 체크</option>
                                <option value="TODAY">출력일 (YYYY-MM-DD)</option>
                                <option value="STATIC">고정값</option>
                              </select>
                            </td>
                            <td className="px-2 py-1">
                              <button
                                type="button"
                                className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                                onClick={() => {
                                  setFields(fields.filter((_, i) => i !== idx))
                                }}
                              >
                                삭제
                              </button>
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="button"
                    className="mt-2 rounded border px-3 py-1 text-sm hover:bg-slate-50"
                    onClick={() => {
                      // 순번 기반 key 생성
                      const existingKeys = fields.map(f => f.key).filter(k => k && k.startsWith('key_'))
                      const maxNum = existingKeys.reduce((max, k) => {
                        const match = k.match(/^key_(\d+)$/)
                        if (match) {
                          const num = parseInt(match[1], 10)
                          return Math.max(max, num)
                        }
                        return max
                      }, 0)
                      const newKey = `key_${maxNum + 1}`
                      setFields([...fields, {
                        key: newKey,
                        bindKey: '',
                        pageIndex: pdfPageIndex,
                        x: 0.1,
                        y: 0.1,
                        w: 0.2,
                        h: 0.03,
                        fontSize: 12,
                        align: 'left',
                        type: 'text',
                        valueType: '',
                        value: '',
                        format: '',
                        fontKey: '',
                      }])
                      setSelectedFieldKey(newKey)
                    }}
                  >
                    필드 추가
                  </button>
                  </>
                  )}
                </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                className="rounded bg-brand-navy text-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-60"
                onClick={handleSave}
                disabled={saving || !templateFileId || fields.length === 0}
              >
                {saving ? '저장 중...' : '템플릿 저장'}
              </button>
            </div>
                      </>
                    )}
                  </div>

                </div>

              </>
            )}
        </div>
        </div>

      </div>
    </div>
  )
}
